const jsotp = require("jsotp");
import { GatsbyLocation } from "local-types";
import React, { useState } from "react";
import { animated } from "react-spring";
import styled from "styled-components";
import Awesome from "../components/Awesome";
import Layout from "../components/Layout";
import SEO from "../components/Seo";
import { usePageTransitions, useSiteMetadata } from "../hooks";

// Styled Components
const TokenContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 2rem;
`;

const TokenForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  width: 100%;

  h2 {
    margin-bottom: 1rem;
    color: #333;
  }

  p {
    margin-bottom: 2rem;
    color: #666;
    line-height: 1.5;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TokenInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #0056b3;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

// TOTP verification using jsotp
function verifyToken(token: string): boolean {
  try {
    const secret =
      "MJRDCYJWGBRDGYZVG4ZWINBRGA3GMYZUMQYDIOBYGRRGINBTGY2WKNRRG44DONZXHFTDEMDGMRTDIMRVGA3DGMBWMU3TAZDBGI2TINBXGFTGKNZSHE2Q====";
    const totp = jsotp.TOTP(secret);

    return totp.verify(token);
  } catch (error) {
    console.error("Token verification error:", error);
    return false;
  }
}

interface AwesomePageProps {
  location: GatsbyLocation;
}

const AwesomePage: React.FunctionComponent<AwesomePageProps> = ({
  location,
}): React.ReactElement => {
  const { title: siteTitle, author } = useSiteMetadata();
  const [token, setToken] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const transitions = usePageTransitions({ location });

  const handleTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setErrorMessage("Please enter a token");
      setShowError(true);
      return;
    }

    const isValid = verifyToken(token);
    if (isValid) {
      setIsVerified(true);
      setShowError(false);
    } else {
      setErrorMessage("Invalid token. Please try again.");
      setShowError(true);
    }
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToken(e.target.value);
    if (showError) {
      setShowError(false);
    }
  };

  return (
    <Layout location={location} title={siteTitle}>
      <SEO title="Amazing World" />
      {transitions.map(({ props, key }) => (
        <animated.div key={key} style={props}>
          {!isVerified ? (
            <TokenContainer>
              <TokenForm onSubmit={handleTokenSubmit}>
                <h2>Enter Access Token</h2>
                <p>
                  Please enter your authentication token to access this content.
                </p>
                <InputGroup>
                  <TokenInput
                    type="text"
                    value={token}
                    onChange={handleTokenChange}
                    placeholder="Enter token here..."
                    autoFocus
                  />
                  <SubmitButton type="submit">Verify</SubmitButton>
                </InputGroup>
                {showError && <ErrorMessage>{errorMessage}</ErrorMessage>}
              </TokenForm>
            </TokenContainer>
          ) : (
            <Awesome />
          )}
        </animated.div>
      ))}
    </Layout>
  );
};

export default AwesomePage;
